import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { themeGet } from 'styled-system';
import PropTypes from 'prop-types';
import { Flex, Box, Heading, Text } from 'roo-ui/components';
import styled from '@emotion/styled';
import PulsingLoadingBlock from 'components/PulsingLoadingBlock';
import { mediaQuery } from 'lib/styledSystem';
import {
  getHasValidQuery,
  getIsLoading,
  getTotalRoomTypeCount,
  getAvailableRoomTypeCount,
} from 'store/propertyAvailability/propertyAvailabilitySelectors';

const SummaryLoader = styled(Flex)`
  height: ${themeGet('space.6')};
  transition: all 0.2s ease-in-out 0.2s;
  opacity: ${(props) => (props.isLoading ? '1' : '0')};
  align-items: center;
  margin-top: -24px;
`;

const SummaryResults = styled(Flex)`
  height: ${themeGet('space.6')};
  transition: all 0.2s ease-in-out 0.4s;
  opacity: ${(props) => (props.isLoading ? '0' : '1')};
  align-items: center;
  font-size: ${themeGet('fontSizes.sm')};
`;

const Summary = ({ isLoading, availableRoomTypeCount, totalRoomTypeCount }) => {
  const hasRooms = availableRoomTypeCount > 0;
  const showingWithResults = hasRooms ? 'Showing ' : '';
  const roomsShownCopy = `${showingWithResults}${availableRoomTypeCount} available out of ${totalRoomTypeCount} rooms`;
  return (
    <Fragment>
      <Flex flexDirection="column" alignItems="center" mb={8} mt={0}>
        <SummaryResults isLoading={isLoading}>
          <Box data-testid="available-room-type-count">{roomsShownCopy}</Box>
          <Box display={['none', 'block']}>&nbsp;for your stay</Box>
        </SummaryResults>
        <SummaryLoader isLoading={isLoading}>
          <PulsingLoadingBlock height={14} width={50} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={20} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={100} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={20} mr={1} mb={0} />
          <PulsingLoadingBlock height={14} width={100} mb={0} />
        </SummaryLoader>
      </Flex>
    </Fragment>
  );
};

Summary.propTypes = {
  availableRoomTypeCount: PropTypes.number,
  totalRoomTypeCount: PropTypes.number,
  isLoading: PropTypes.bool,
};

Summary.defaultProps = {
  availableRoomTypeCount: 0,
  totalRoomTypeCount: 0,
  isLoading: false,
};

const SummaryContainer = styled(Box)`
  min-height: 20px;
  text-align: center;
  padding-top: ${themeGet('space.6')};

  ${mediaQuery.minWidth.sm} {
    padding-top: 0px;
    min-height: 60px;
  }
`;

SummaryContainer.displayName = 'SummaryContainer';

class PropertyAvailabilitySummary extends PureComponent {
  render() {
    const { hasQuery, totalRoomTypeCount, availableRoomTypeCount, propertyName, isLoading } = this.props;

    if (!hasQuery) return null;

    return (
      <Fragment>
        <SummaryContainer>
          <Text fontSize="base" color="greys.steel" data-testid="property-name">
            {propertyName}
          </Text>
          {isLoading || availableRoomTypeCount > 0 ? (
            <Heading.h2 data-testid="property-availability-header" mb={3} fontSize="xl">
              Choose your room
            </Heading.h2>
          ) : (
            <Heading.h2 data-testid="property-availability-header" fontSize="xl">
              We couldn&apos;t find any available rooms
            </Heading.h2>
          )}
          <Summary isLoading={isLoading} availableRoomTypeCount={availableRoomTypeCount} totalRoomTypeCount={totalRoomTypeCount} />
        </SummaryContainer>
      </Fragment>
    );
  }
}

PropertyAvailabilitySummary.propTypes = {
  availableRoomTypeCount: PropTypes.number.isRequired,
  totalRoomTypeCount: PropTypes.number.isRequired,
  propertyName: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  hasQuery: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  hasQuery: getHasValidQuery(state),
  isLoading: getIsLoading(state),
  totalRoomTypeCount: getTotalRoomTypeCount(state),
  availableRoomTypeCount: getAvailableRoomTypeCount(state),
});

export default connect(mapStateToProps)(PropertyAvailabilitySummary);
